'use client';

import type { IEnglishIqItem } from '@repo/sdk';
import { useEffect, useRef } from 'react';
import type { EnglishIqMessagesType } from '../english-iq-messages-type';
import { EnglishIqAnswer } from './answer';
import { EnglishIqQuestionProgress } from './english-iq-question-progress';
import { getEnglishIqQuestionProgress } from './get-english-iq-question-progress';
import { isVideoMedia } from './is-video-media';
import { useEnglishIqElapsedSeconds } from './use-english-iq-elapsed-seconds';

interface IEnglishIqQuestionProps {
  index: number;
  isBusy: boolean;
  item: IEnglishIqItem;
  messages: EnglishIqMessagesType['wizard'];
  onAnswer: (choice: string) => void;
  startedAt: number;
}

function ListeningMedia({ url }: { url: string }): React.JSX.Element {
  if (!isVideoMedia(url)) {
    return (
      <audio className="mt-5 w-full" controls preload="metadata" src={url}>
        <track kind="captions" />
      </audio>
    );
  }
  return (
    <video
      className="mt-5 max-h-[42svh] w-full rounded-2xl bg-black object-contain"
      controls
      playsInline
      preload="metadata"
      src={url}
    >
      <track kind="captions" />
    </video>
  );
}

function getInstruction(item: IEnglishIqItem, messages: EnglishIqMessagesType['wizard']): string {
  if (item.skill === 'listening') {
    return messages.listen;
  }
  if (item.type === 'reorder') {
    return messages.reorder;
  }
  if (item.type === 'error_fix') {
    return messages.fixSentence;
  }
  if (item.type === 'cloze' && item.choices.length === 0) {
    return messages.fillBlank;
  }
  return messages.choose;
}

export function EnglishIqQuestion({
  index,
  isBusy,
  item,
  messages,
  onAnswer,
  startedAt,
}: IEnglishIqQuestionProps): React.JSX.Element {
  const promptRef = useRef<HTMLHeadingElement>(null);
  const elapsedSeconds = useEnglishIqElapsedSeconds(startedAt);
  const { minutes, progress } = getEnglishIqQuestionProgress(elapsedSeconds);
  const instruction = getInstruction(item, messages);

  useEffect(() => {
    promptRef.current?.focus();
  }, []);

  return (
    <div className="mx-auto w-full max-w-2xl">
      <EnglishIqQuestionProgress
        index={index}
        messages={messages}
        minutes={minutes}
        progress={progress}
        skillLabel={messages[item.skill]}
      />
      <div>
        <p className="text-sm font-medium text-muted-foreground">{instruction}</p>
        {item.skill === 'listening' && item.mediaUrl ? (
          <ListeningMedia url={item.mediaUrl} />
        ) : null}
        <h2
          ref={promptRef}
          tabIndex={-1}
          className="mt-5 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl"
          dir="auto"
        >
          {item.prompt}
        </h2>
      </div>
      <div className="mt-6 sm:mt-8">
        <EnglishIqAnswer
          key={item.id}
          disabled={isBusy}
          item={item}
          messages={messages}
          onAnswer={onAnswer}
        />
      </div>
    </div>
  );
}
